import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import {
  Appointment,
  Dossier,
  Fitter,
  getLocationData,
} from '@sc/data-access-models';
import {
  AppointmentsSandboxService,
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { StorageService } from '@sc/data-access-storage';
import {
  TabTitleStrategyService,
  TranslatorService,
} from '@sc/data-access-translation';
import {
  AppointmentSubscriptionBuilder,
  AppointmentsSubService,
} from '@sc/feature-appointments-subscription';
import { AuthService } from '@sc/feature-auth-okta';
import { DialogService } from '@sc/feature-dialog';
import { FitterPageComponent } from '@sc/feature-fitter-page';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { SnackbarService } from '@sc/feature-snackbar';
import { TagManagerService } from '@sc/feature-tag-manager';
import { RegisterAppService } from '@sc/register-app-actions';
import { IconComponent } from '@sc/ui-components';
import { DeclarationConditions } from '@sc/utils';
import { Buffer } from 'buffer';
import { Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { InsurerDeclarationSteps } from '../../../../models';

@Component({
  selector: 'sc-insurer-fitter-page',
  styleUrls: ['./insurer-fitter-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [AppointmentSubscriptionBuilder],
  imports: [FitterPageComponent, IconComponent, TranslateModule],
  template: `
    <sc-fitter-page
      [fitter]="fitter"
      [displayTimeFilters]="true"
      [displayComments]="false"
      [loading]="loading"
      (clickReturn)="onReturnPreviousPage($event)"
      (showOpeningsRedirect)="onRedirectMobileOpeningPeriods()"
      (updateItinerary)="onChangeItinerary($event)"
      (book)="onBookAppointment($event)"
      (requestAppointment)="onRequestAppointment($event)"
      (clickChoseOtherFitter)="onChoseOtherFitter()"
    >
      <ng-template #linkBack let-onGoBack="onGoBack">
        <span
          tabindex="0"
          role="button"
          class="linkBack sc-design-link-bold"
          tabindex="0"
          role="link"
          (click)="onGoBack()"
          (keydown.enter)="onGoBack()"
          (keydown.space)="onGoBack()"
        >
          <sc-icon name="arrow_left"></sc-icon>
          {{ 'Fitter.backLinkLabel' | translate }}
        </span>
      </ng-template>
    </sc-fitter-page>
  `,
})
export class InsurerFitterPageComponent implements OnInit, OnDestroy {
  fitter?: Fitter;
  appointments: Appointment[] = [];
  currentDossier: Dossier;
  itinerary = { meters: 0, seconds: 0 };
  loading = true;

  private loadFitterSub?: Subscription;
  private fetchFittersSub?: Subscription;
  private loadAppointmentsSub?: Subscription;

  private readonly appointmentSub: AppointmentsSubService;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly appointmentSubBuilder: AppointmentSubscriptionBuilder,
    private readonly appointmentsSB: AppointmentsSandboxService,
    private readonly dossierSB: DossierSandboxService,
    private readonly sandbox: SandboxService,
    private readonly loadingService: LoadingService,
    private readonly changeRef: ChangeDetectorRef,
    private readonly translator: TranslatorService,
    private readonly configService: ConfigurationService,
    private readonly featureFlippingService: FeatureFlippingManagerService,
    private readonly authService: AuthService,
    private readonly storage: StorageService,
    private readonly snackbarService: SnackbarService,
    private readonly dialog: DialogService,
    private readonly insightService: InsightService,
    private readonly tmService: TagManagerService,
    private readonly tabTitleService: TabTitleStrategyService,
    private readonly declarationConditions: DeclarationConditions,
    private readonly registerAppService: RegisterAppService
  ) {
    this.insightService.trackPageView({
      name: 'Insurer fitter page',
    });

    this.appointmentSub = this.appointmentSubBuilder
      .fetchTimeout(15000)
      .build();
    this.initNavigation();
  }

  initNavigation() {
    this.sandbox.setCurrentStep(InsurerDeclarationSteps.hideStep);
  }

  ngOnInit(): void {
    this.snackbarService.setFocusManagement(true, {
      documentQuery: 'sc-insurer-fitter-page sc-fitter-page',
    });

    this.loading = true;

    this.appointmentSub.initSubscriptions();

    this.appointmentSub.dossierSB.dossier$.subscribe(
      (newDossier) => (this.currentDossier = newDossier)
    );

    this.route.params.subscribe((params) => {
      const newFitterId = params?.fitterId;
      const newBrandId = params?.brandId;

      this.loadFitter(newFitterId, newBrandId);
      this.loadAppointments(newFitterId);
    });
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
    this.loadFitterSub?.unsubscribe();
    this.loadAppointmentsSub?.unsubscribe();
    this.appointmentSub.destroy();
    this.fetchFittersSub.unsubscribe();
  }

  private loadFitter(fitterId: string, brandId: string) {
    this.loadFitterSub?.unsubscribe();

    this.loadFitterSub = this.appointmentSub.fitters$.subscribe((fitters) => {
      this.fitter = fitters.find((f) => f.id === fitterId) ?? this.fitter;

      this.tabTitleService.setPagination({
        key: 'GlobalApplicativeConstants.tabTitles.paginations.fitterName',
        params: { fitterName: `${this.fitter?.brand} - ${this.fitter?.city}` },
      });

      this.loading = false;

      this.changeRef.detectChanges();
    });

    this.fetchFittersSub = this.appointmentSub.fittersSB
      .fetchFitter({ id: fitterId, brandId: brandId })
      .pipe(
        switchMap((fitter) =>
          this.appointmentSub.fittersSB.addFitters([fitter])
        ),
        take(1)
      )
      .subscribe(() => this.launchAppointmentSubscription());
  }

  private loadAppointments(fitterId: string) {
    this.loadAppointmentsSub?.unsubscribe();

    this.loadAppointmentsSub =
      this.appointmentSub.appointmentsByFitter$.subscribe(
        (appointments) => (this.appointments = appointments.get(fitterId))
      );
  }

  private launchAppointmentSubscription() {
    if (!this.fitter) {
      return;
    }

    this.appointmentSub
      .fetchFreeSlots({
        searchDates: {
          startDate: new Date(),
          endDate: null,
        },
        claim: this.currentDossier?.claim,
        searchLocation: {
          distance: 20,
          address:
            this.currentDossier?.searchCriteria?.address ||
            getLocationData(this.fitter),
          mapCenter: this.fitter.location,
        },
        fitterId: this.fitter.id,
        outsideIntervention: null,
        replacementVehicle: true,
        returnFittersOnly: !this.declarationConditions.canBookAppointment(
          this.currentDossier?.claim?.glass
        ),
      })
      .catch((error) => console.error(error));
  }

  onReturnPreviousPage(isModification) {
    if (isModification) {
      this.router
        .navigate(['/declaration/declaration-end'])
        .catch((error) => console.error(error));
    } else {
      this.router
        .navigate(['/declaration/intervention'])
        .catch((error) => console.error(error));
    }
  }

  onChoseOtherFitter() {
    this.router
      .navigate(['/declaration/intervention'])
      .catch((error) => console.error(error));
  }

  onRedirectMobileOpeningPeriods() {
    this.router
      .navigate([`/declaration/openings/${this.fitter.id}`], {
        queryParams: { ...this.itinerary },
      })
      .catch((error) => console.error(error));
  }

  onChangeItinerary(newItinerary: { meters: number; seconds: number }) {
    this.itinerary = newItinerary;
  }

  navigateToNextStep(
    encodedParamToPush: string,
    afterNavigate: (fulfilled: boolean) => void
  ) {
    if (
      this.featureFlippingService.isFeatureActivated(
        'b2b-redirect-after-slot-select'
      )
    ) {
      this.registerAppService.deactivateRegisterAppClose();

      window.open(
        (this.storage.getItem('redirect-uri') ||
          this.configService.getConfig().website) +
          (encodedParamToPush ? '&dossier=' + encodedParamToPush : ''),
        '_self'
      );
      return;
    }

    this.router
      .navigate(['/', 'declaration', 'declaration-end'])
      .then((fulfilled) => afterNavigate(fulfilled))
      .catch((error) => console.error(error));
  }

  onBookAppointment(bookResult: { dossier: Dossier; modified: boolean }) {
    this.loadingService.setLoading(true);

    let bookingSub;

    if (bookResult.modified) {
      bookingSub = this.appointmentsSB.modifyAppointment({
        dossier: bookResult.dossier,
      });
    } else {
      bookingSub = this.appointmentsSB.bookAppointment({
        dossier: bookResult.dossier,
      });
    }

    bookingSub.subscribe(
      (result) => {
        this.tmService.pushTag({
          event: 'appointment',
          dealer_name: this.fitter.brand,
          dealer_location: this.fitter.city,
          license_plate: bookResult.dossier.claim.car.licensePlateNumber,
          vehicle_damage: bookResult.dossier.claim.glass.glass,
        });
        this.insightService.trackEvent({
          name: `FrontApp [Insurer-Action] successfully ${
            bookResult.modified ? 'rebook dossier' : 'book new dossier '
          } from fitter page`,
          properties: {
            dossier: bookResult.dossier,
          },
        });
        this.loadingService.setLoading(false);
        this.authService.removeDossierAccess();
        let encodedParamToPush = null;
        if (
          this.featureFlippingService.isFeatureActivated(
            'integ-contract-manager-redirect-dossier-by-param'
          ) &&
          result.dossierForContractManager
        ) {
          // use this to push to the redirect_uri throught the encodedDossier param
          encodedParamToPush = Buffer.from(
            JSON.stringify(result.dossierForContractManager)
          ).toString('base64');
        }
        this.navigateToNextStep(encodedParamToPush, (fulfilled) => {
          if (fulfilled) {
            this.snackbarService.showNotification(
              this.translator.getInstantTranslation(
                'DeclarationEnd.bookAppointmentValidationTitle'
              ),
              this.translator.getInstantTranslation(
                'DeclarationEnd.bookAppointmentValidationText'
              ),
              10
            );
          }
        });
      },
      (err) => {
        this.loadingService.setLoading(false);
        this.onBookAppointmentError(err);
      }
    );
  }

  onRequestAppointment(event: { dossier: Dossier }) {
    this.loadingService.setLoading(true);

    this.appointmentsSB
      .requestAppointment({ dossier: event.dossier })
      .pipe(switchMap((result) => this.dossierSB.setDossier(result.dossier)))
      .subscribe(
        () => {
          this.insightService.trackEvent({
            name: `FrontApp [Insurer-Action] successfully requested appointment from fitter page`,
            properties: {
              dossier: event.dossier,
            },
          });
          this.loadingService.setLoading(false);
          this.navigateToNextStep(null, (fulfilled) => {
            if (fulfilled) {
              this.snackbarService.showNotification(
                this.translator.getInstantTranslation(
                  'DeclarationEnd.requestAppointmentValidationTitle'
                ),
                this.translator.getInstantTranslation(
                  'DeclarationEnd.requestAppointmentValidationText'
                ),
                10
              );
            }
          });
        },
        (err) => {
          this.loadingService.setLoading(false);
          this.onBookAppointmentError(err);
        }
      );
  }

  onBookAppointmentError(error: any) {
    // Book en echec, donc on retire l'appointment du dossier
    this.currentDossier = {
      ...this.currentDossier,
      appointment: null,
    };
    const errorMessageContent = error?.error?.message
      ? this.translator.getInstantTranslation(error?.error?.message)
      : this.translator.getInstantTranslation('Booking.errors.generic');

    this.dialog.showError(
      this.translator.getInstantTranslation(
        'InsurerDeclarationIntervention.reservation'
      ),
      errorMessageContent
    );
  }
}
